import React from "react";
import {
    CardContent,
    Box,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Grid,
    CircularProgress,
} from "@mui/material";
import {
    StyledCard,
    StyledTypography,
    StyledDivider,
} from "components/Screens/Company/CompanySummary";
import { MeetingNotesSummary } from "../types";
import Markdown from "components/Common/Markdown/Markdown";
import {
    useMeetingNotesCategories,
    useSelectedNote,
    useSentimentMovingAverage,
} from "../hooks";
import { useChartData, useChart } from "../chart";
import { formatDate } from "utils/date.utils";
import { useParams } from "react-router-dom";

export const ALL_CATEGORIES_VALUE = "all";

interface Props {
    meetingNotes: MeetingNotesSummary[];
    companySectorName: string | null;
}

const MeetingHistorySection: React.FC<Props> = ({
    meetingNotes,
    companySectorName,
}) => {
    const { company_id: companyId } = useParams<{ company_id?: string }>();

    const categories = useMeetingNotesCategories();
    const allAvailableCategories = React.useMemo(
        () => categories.data.map((cat) => cat.name),
        [categories.data]
    );
    const movingAverage = useSentimentMovingAverage(
        companyId ?? "",
        categories.selected,
        categories.data,
        meetingNotes
    );
    const chartData = useChartData(
        meetingNotes,
        categories.selected,
        allAvailableCategories
    );
    const { handleMarkerClick, displayNote } = useSelectedNote(
        categories.selected,
        meetingNotes
    );
    const { chartRef } = useChart(
        chartData,
        movingAverage.data,
        handleMarkerClick,
        !movingAverage.loading,
        companySectorName
    );

    if (!meetingNotes || meetingNotes.length === 0) {
        return null;
    }

    return (
        <Grid container spacing={2}>
            <Grid item xs={12} md={8}>
                <StyledCard>
                    <CardContent>
                        <StyledTypography
                            variant="h6"
                            color="primary"
                            fontWeight="bold"
                        >
                            Meeting History
                        </StyledTypography>

                        <StyledDivider sx={{ my: 2 }} />

                        <Box>
                            <FormControl
                                size="small"
                                sx={{ minWidth: { xs: "100%", sm: 200 } }}
                            >
                                <InputLabel>Filter by Category</InputLabel>
                                <Select
                                    value={categories.selected}
                                    onChange={(e) =>
                                        categories.onCategoryChange(
                                            e.target.value
                                        )
                                    }
                                    label="Filter by Category"
                                    disabled={categories.loading}
                                >
                                    <MenuItem value={ALL_CATEGORIES_VALUE}>
                                        All Categories
                                    </MenuItem>
                                    {categories.data.map((category) => (
                                        <MenuItem
                                            key={category.id}
                                            value={category.name}
                                        >
                                            {category.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>

                        {movingAverage.loading ? (
                            <Box
                                display="flex"
                                justifyContent="center"
                                alignItems="center"
                                height={{ xs: 400, md: 300 }}
                                color="primary"
                            >
                                <CircularProgress />
                            </Box>
                        ) : (
                            <Box
                                position="relative"
                                height="100%"
                                minHeight={{ xs: 400, md: 300 }}
                            >
                                {chartData ? (
                                    <div
                                        ref={chartRef}
                                        style={{
                                            width: "100%",
                                            height: "100%",
                                        }}
                                    />
                                ) : (
                                    <Box
                                        display="flex"
                                        justifyContent="center"
                                        alignItems="center"
                                        height={{ xs: 400, md: 300 }}
                                        color="text.secondary"
                                    >
                                        <StyledTypography variant="body2">
                                            No meeting notes found for the
                                            selected category
                                        </StyledTypography>
                                    </Box>
                                )}
                            </Box>
                        )}
                    </CardContent>
                </StyledCard>
            </Grid>
            {displayNote && (
                <Grid item xs={12} md={4}>
                    <StyledCard>
                        <CardContent>
                            <StyledTypography
                                variant="h6"
                                color="primary"
                                fontWeight="bold"
                            >
                                {displayNote.meeting_note_title}
                            </StyledTypography>

                            <StyledTypography
                                variant="body2"
                                color="textSecondary"
                                mt={1}
                                mb={2}
                            >
                                {formatDate(
                                    new Date(displayNote.meeting_note_date)
                                )}
                            </StyledTypography>

                            <Markdown
                                text={displayNote.summary_markdown}
                                fontSize="16px"
                            />
                        </CardContent>
                    </StyledCard>
                </Grid>
            )}
        </Grid>
    );
};

export default MeetingHistorySection;
